// ** MUI Imports
import { styled, useTheme } from '@mui/material/styles'
import Box, { BoxProps } from '@mui/material/Box'
import MenuIcon from '@mui/icons-material/Menu';

// ** Types
import { BlankLayoutWithAppBarProps } from './types'
import { useSettings } from 'src/@core/hooks/useSettings'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Theme } from '@mui/material/styles'
import MuiToolbar, { ToolbarProps } from '@mui/material/Toolbar'

// ** AppBar Imports
import AppBarContent from './components/horizontal/AppBarContent'
import AppBar from '@mui/material/AppBar'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import React, { useEffect, useRef, useState } from 'react'
import authService from "../components/api-authorization/AuthorizeService"

import { useDispatch } from 'react-redux'
import { fetchLookups } from 'src/store/apps/lookup'
import { fetchAlerts } from 'src/store/apps/alert'
import { AppDispatch } from 'src/store'
import Sidebar from '../components/sidebar/Sidebar'
import { Divider, IconButton } from '@mui/material'
import { alpha } from '@mui/material/styles'

const HEADER_HEIGHT = 64;
const SIDEBAR_WIDTH = 240;
const FLOAT_GAP = 16; 

// Styled component for Blank Layout with AppBar component
const BlankLayoutWithAppBarWrapper = styled(Box)<BoxProps>(({ theme }) => ({
    height: '100vh',

    // For V1 Blank layout pages
    '& .content-center': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(5),
        minHeight: `calc(100vh - ${theme.spacing((theme.mixins.toolbar.minHeight as number) / 4)})`
    },

    // For V2 Blank layout pages
    '& .content-right': {
        display: 'flex',
        overflowX: 'hidden',
        position: 'relative',
        minHeight: `calc(100vh - ${theme.spacing((theme.mixins.toolbar.minHeight as number) / 4)})`
    }
}))

const Toolbar = styled(MuiToolbar)<ToolbarProps>(({ theme }) => ({
    width: '100%',
    padding: `${theme.spacing(0, 3)} !important`,
    [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5)
    },
    [theme.breakpoints.down('xs')]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
    }
}))

const StyledSidebar = styled('div')(({ theme }) => ({
    width: SIDEBAR_WIDTH,
    position: 'fixed',
    top: HEADER_HEIGHT,
    left: 0,
    height: `calc(100% - ${HEADER_HEIGHT}px)`,
    //backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

const ContentWrapper = styled('main', {
    shouldForwardProp: (prop) => prop !== 'collapsed'
})<{collapsed : boolean}>(({ theme, collapsed }) => ({
    //marginTop: HEADER_HEIGHT,
    marginTop: '14px',
    marginLeft: collapsed ? 70 : SIDEBAR_WIDTH + 10,
    paddingTop: `calc(${HEADER_HEIGHT}px + ${FLOAT_GAP}px)`,
    padding: theme.spacing(3),
    width: `calc(100% - ${collapsed ? 70 : SIDEBAR_WIDTH + 10}px)`,
    //height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    display: 'flex',
    flexDirection: "column",
    //alignItems: 'center',
    //justifyContent: 'center',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        width: '100%',
    },
}));

const BlankLayoutWithAppBar = (props: BlankLayoutWithAppBarProps) => {
    // ** Props
    const { children } = props
    const { settings, saveSettings } = useSettings();
    const { skin, appBar, navHidden, appBarBlur } = settings
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch<AppDispatch>();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    // Function to toggle the sidebar on small screens
    const handleDrawerToggle = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    useEffect(() => {
        dispatch(fetchLookups());
        dispatch(fetchAlerts());
    }, [dispatch])

    /**
     *  The below variable will hide the current layout menu at given screen size.
     *  The menu will be accessible from the Hamburger icon only (Vertical Overlay Menu).
     *  You can change the screen size from which you want to hide the current layout menu.
     *  Please refer useMediaQuery() hook: https://mui.com/material-ui/react-use-media-query/,
     *  to know more about what values can be passed to this hook.
     *  ! Do not change this value unless you know what you are doing. It can break the template.
    */
    const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState({
        FirstName: "",
        LastName: "",
        name: ""
    });
    let _subscription = useRef(0);

    useEffect(() => {
        _subscription.current = authService.subscribe(() => populateState());
        populateState();
        console.log("");
        return (() => {
            authService.unsubscribe(_subscription.current);
        })
    }, []);

    const populateState = async () => {
        const [isUserAuthenticated, user] = await Promise.all([authService.isAuthenticated(), authService.getUser()])
        setIsAuthenticated(isUserAuthenticated);
        setUser(user);
    }

    return (
        <BlankLayoutWithAppBarWrapper>

            <AppBar
                color='default'
                elevation={0}
                className='layout-navbar-and-nav-container'
                position={appBar === 'fixed' ? 'sticky' : 'static'}
                //position={"fixed"}
                sx={{
                    alignItems: 'center',
                    color: 'text.primary',
                    justifyContent: 'center',
                    ...(appBar === 'static' && { zIndex: 13 }),
                    //backgroundColor: (theme: any) => theme.palette.background.paper,
                    backgroundColor: 'transparent',
                    top: `${FLOAT_GAP}px`,
                    left: `${FLOAT_GAP}px`,
                    right: `${FLOAT_GAP}px`,
                    width: `calc(100% - ${FLOAT_GAP * 2}px)`,
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.08)}`,
                    ...(skin === 'bordered' && { borderBottom: (theme: any) => `1px solid ${theme.palette.divider}` }),
                    transition: 'border-bottom 0.2s ease-in-out, backdrop-filter .25s ease-in-out, box-shadow .25s ease-in-out',
                    ...(appBar === 'fixed'
                        ? appBarBlur && {
                            backdropFilter: 'blur(8px)',
                            backgroundColor: (theme: any) => hexToRGBA(theme.palette.background.paper, 0.9)
                        }
                        : {})
                }}
            >
                {/* Navbar / AppBar */}
                <Box
                    className='layout-navbar'
                    sx={{
                        width: '100%',
                        borderRadius: 4,
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.88),
                        backdropFilter: 'blur(10px)',
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    }}
                >
                    <Toolbar
                        className='horizontal-nav-content-container'
                        sx={{
                            mx: 'auto',
                            minHeight: '52px !important',
                            //minHeight: (theme: any) => `${(theme.mixins.toolbar.minHeight as number) - 1}px !important`
                        }}
                    >
                        {isSmallScreen && (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <AppBarContent {...props} settings={settings} saveSettings={saveSettings} user={user} userAuthenticated={isAuthenticated} />
                    </Toolbar>
                    <Divider
                        sx={{
                            //mx: 6, // Adds horizontal margin on left and right
                            borderColor: (theme) => alpha(theme.palette.divider, 0.35),
                            //borderRadius: "48px",
                            borderWidth: '1px',
                        }}
                    />
                </Box>
            </AppBar>
            <StyledSidebar>
                <Sidebar
                    open={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(!isSidebarOpen)}
                    headerHeight={HEADER_HEIGHT}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    variant={isSmallScreen ? 'temporary' : 'permanent'}
                />
            </StyledSidebar>

            {/* Main Content Area */}
            <ContentWrapper collapsed={collapsed}>
                {React.Children.map(children, child => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child as React.ReactElement<any>, { user });
                    }
                    return child;
                })}
            </ContentWrapper>
        </BlankLayoutWithAppBarWrapper>
    )
}

export default BlankLayoutWithAppBar
